import { HTMLAttributes, type MouseEvent, ReactNode, useMemo, useState } from "react";
import { ScaleLoader } from "react-spinners";
import { Modal } from "../layout/Modal";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";

/**
 * @deprecated Use `Button` from `@/components/primitives/Button` instead.
 * This is a legacy component; new code should not import from `ui/`.
 * See `apps/portal/src/components/primitives/AGENTS.md` for the new contract.
 */
export type ButtonColor =
	| "primary"
	| "secondary"
	| "confirm"
	| "warning"
	| "danger"
	| "inverse"
	| "brand"
	| "none";

/**
 * @deprecated Use `Button` from `@/components/primitives/Button` instead.
 * This is a legacy component; new code should not import from `ui/`.
 * See `apps/portal/src/components/primitives/AGENTS.md` for the new contract.
 */
export type ButtonProps = {
	testId?: string;
	color: ButtonColor;
	type?: HTMLButtonElement["type"];
	form?: string;
	disabled?: boolean;
	condensed?: boolean;
	children?: ReactNode;
	tabIndex?: number;
	className?: string;
	style?: HTMLAttributes<HTMLDivElement>["style"];
	rounded?: boolean;
	filled?: boolean;
	shadow?: boolean;
	thinFont?: boolean;
	width?: string;
	fullWidth?: boolean;
	confirmationModal?: boolean;
	confirmationMessage?: string;
	confirmationMessageSubtext?: ReactNode;
	loading?: boolean;
	onClick?: (event?: MouseEvent) => void;
	trackingId?: string;
};

const getClassName = (
	props: Pick<
		ButtonProps,
		"thinFont" | "color" | "filled" | "shadow" | "rounded" | "className" | "disabled" | "condensed"
	>,
) => {
	let className =
		"h-[34px] shrink-0 whitespace-nowrap min-w-fit flex items-center justify-center px-[16px] text-sm cursor-pointer select-none bg-transparent";

	if (props.thinFont) {
		className += " font-medium";
	} else {
		className += " font-semibold";
	}

	if (props.color === "primary") {
		if (props.filled) {
			className +=
				" text-inverse bg-button-primary enabled:hover:bg-button-primary-hover enabled:active:bg-button-primary-active";
		} else {
			className +=
				" border border-button-primary text-button-primary enabled:hover:border-button-primary-hover enabled:hover:text-button-primary-hover enabled:active:border-button-primary-active enabled:active:text-button-primary-active";
		}

		if (props.shadow) {
			className += " shadow-[2px_2px_0_rgba(71,124,204,0.25)]";
		}
	} else if (props.color === "secondary") {
		if (props.filled) {
			className +=
				" text-inverse bg-button-secondary enabled:hover:bg-button-secondary-hover enabled:active:bg-button-secondary-active";
		} else {
			className +=
				" border border-button-secondary text-secondary enabled:hover:border-button-secondary-hover enabled:active:border-button-secondary-active";
		}
	} else if (props.color === "inverse") {
		if (props.filled) {
			className +=
				" text-primary bg-button-inverse enabled:hover:bg-button-inverse-hover enabled:active:bg-button-inverse-active";
		} else {
			className +=
				" border border-button-inverse text-button-inverse enabled:hover:border-button-inverse-hover enabled:hover:text-button-inverse-hover enabled:active:border-button-inverse-active enabled:active:text-button-inverse-active";
		}
	} else if (props.color === "brand") {
		if (props.filled) {
			className +=
				" text-inverse bg-brand-primary enabled:hover:bg-brand-light enabled:active:bg-brand-lighter";
		} else {
			className +=
				" border border-brand-primary text-brand-primary enabled:hover:border-brand-light enabled:hover:text-brand-light enabled:active:border-brand-lighter enabled:active:text-brand-lighter";
		}
	} else if (props.color === "confirm") {
		if (props.filled) {
			className +=
				" text-inverse bg-button-confirm enabled:hover:bg-button-confirm-hover enabled:active:bg-button-confirm-active";
		} else {
			className +=
				" border border-button-confirm text-button-confirm enabled:hover:border-button-confirm-hover enabled:hover:text-button-confirm-hover enabled:active:border-button-confirm-active enabled:active:text-button-confirm-active";
		}
	} else if (props.color === "warning") {
		if (props.filled) {
			className +=
				" text-secondary bg-button-warning enabled:hover:bg-button-warning-hover enabled:active:bg-button-warning-active";
		} else {
			className +=
				" border border-button-warning text-button-warning enabled:hover:border-button-warning-hover enabled:hover:text-button-warning-hover enabled:active:border-button-warning-active enabled:active:text-button-warning-active";
		}
	} else if (props.color === "danger") {
		if (props.filled) {
			className +=
				" text-inverse bg-button-danger enabled:hover:bg-button-danger-hover enabled:active:bg-button-danger-active";
		} else {
			className +=
				" border border-button-danger text-button-danger enabled:hover:border-button-danger-hover enabled:hover:text-button-danger-hover enabled:active:border-button-danger-active enabled:active:text-button-danger-active";
		}
		// TODO: Remove "none" color after migrating to icon library
	} else if (props.color === "none") {
		if (props.filled) {
			className += "enabled:hover:brightness-[98%]";
		} else {
			className += " enabled:hover";
		}
	}

	if (props.rounded) {
		className += " rounded-full";
	} else {
		className += " rounded-[4px]";
	}

	if (props.disabled) {
		className += " cursor-not-allowed hover:default";
	}

	if (props.condensed) {
		className += " px-3 text-xs";
	}

	return cn(className, props.className);
};

const getLoaderColor = (color: ButtonProps["color"]) => {
	switch (color) {
		case "primary":
		case "confirm":
			return "white";
		case "secondary":
			return "#1f2937";
	}
};

/**
 * @deprecated Use `Button` from `@/components/primitives/Button` instead.
 * This is a legacy component; new code should not import from `ui/`.
 * See `apps/portal/src/components/primitives/AGENTS.md` for the new contract.
 */
export const Button = ({
	testId,
	thinFont,
	color,
	filled,
	shadow,
	rounded,
	condensed,
	children,
	width,
	fullWidth,
	confirmationModal,
	confirmationMessage,
	confirmationMessageSubtext,
	loading,
	trackingId,
	...props
}: ButtonProps) => {
	const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
	const className = useMemo(
		() =>
			getClassName({
				thinFont,
				color,
				filled,
				shadow,
				rounded,
				condensed,
				className: props.className,
			}),
		[thinFont, color, filled, shadow, rounded, condensed, props.className],
	);

	const handleConfirmationClick = (e?: MouseEvent) => {
		setShowConfirmationModal(false);
		if (props.onClick) {
			props.onClick(e);
		}
	};

	const handleClick = (e: MouseEvent) => {
		if (confirmationModal) {
			setShowConfirmationModal(true);
		} else if (props.onClick && !props.disabled && !loading) {
			props.onClick(e);
		}
	};

	return (
		<>
			<button
				data-testid={testId}
				{...trackingAttrs(trackingId)}
				tabIndex={1}
				{...props}
				type={props.type ?? "button"}
				className={cn(className, props.disabled ? "opacity-[0.45] cursor-default" : undefined)}
				onClick={handleClick}
			>
				{!loading && children}
				{loading && <ScaleLoader color={getLoaderColor(color)} height={12} width={3} />}
			</button>
			{showConfirmationModal && (
				<Modal>
					<div className="py-[38px] px-[60px] bg-white flex flex-col items-center justify-center rounded-md shadow-lg space-y-[45px]">
						<div className="flex flex-col items-center space-y-[15px]">
							<div className="text-[19px] text-primary font-normal w-full flex justify-center">
								{confirmationMessage}
							</div>
							<div className="text-[16px] w-[90%] text-secondary font-thin text-center">
								{confirmationMessageSubtext}
							</div>
						</div>
						<div className="w-fit flex space-x-[8px]">
							<Button
								color="secondary"
								className="w-[125px]"
								thinFont
								onClick={() => setShowConfirmationModal(false)}
							>
								Cancel
							</Button>
							<Button
								color="primary"
								className="w-[125px]"
								filled
								thinFont
								onClick={handleConfirmationClick}
							>
								Confirm
							</Button>
						</div>
					</div>
				</Modal>
			)}
		</>
	);
};
